import { useRectNodeConnections } from "./RectNodeConnectionsContext";
import { CollapsibleToolGroup } from "../reusable/CollapsibleToolGroup";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { PresetSelector } from "../reusable/PresetSelector";
import { ToolDescriptionPopover } from "../reusable/ToolDescriptionPopover";
import { CheckboxField } from "../reusable/CheckboxField";
import { SendSvgToToolMenu } from "../reusable/SendSvgToToolMenu";
import { Switch } from "../reusable/Switch";
import { Link2, Palette, Paintbrush, Share2 } from "lucide-react";
import { UndoRedoControls } from "../reusable/UndoRedoControls";

export function RectNodeConnectionsControls() {
    const {
        connectionLinkMode,
        connectionDistanceThreshold,
        brushSize,
        brushDensity,
        nodeBaseSize,
        nodeSizeRandomness,
        nodeWidthHeightRandomness,
        nodeColor,
        nodeFillEnabled,
        strokeColor,
        connectionOpacity,
        seed,
        nodes,
        setConnectionLinkMode,
        setConnectionDistanceThreshold,
        setBrushSize,
        setBrushDensity,
        setNodeBaseSize,
        setNodeSizeRandomness,
        setNodeWidthHeightRandomness,
        setNodeColor,
        setNodeFillEnabled,
        setStrokeColor,
        setConnectionOpacity,
        setSeed,
        clearNodes,
        buildExportSvgString,
        handleDownload,
        applyPreset,
        getPresetValues,
        resetParameters,
        undo,
        redo,
        canUndo,
        canRedo,
    } = useRectNodeConnections();

    const handleConnectionDistanceChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue) || numValue <= 0) return;
        setConnectionDistanceThreshold(numValue);
    };

    const handleBrushSizeChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue) || numValue < 0) return;
        setBrushSize(numValue);
    };

    const handleBrushDensityChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue) || numValue < 0) return;
        setBrushDensity(numValue);
    };

    const handleNodeBaseSizeChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue) || numValue <= 0) return;
        setNodeBaseSize(numValue);
    };

    const handleNodeSizeRandomnessChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue)) return;
        const clamped = Math.max(0, Math.min(1, numValue));
        setNodeSizeRandomness(clamped);
    };

    const handleNodeWidthHeightRandomnessChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue)) return;
        const clamped = Math.max(0, Math.min(1, numValue));
        setNodeWidthHeightRandomness(clamped);
    };

    const handleConnectionOpacityChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue)) return;
        const clamped = Math.max(0, Math.min(1, numValue));
        setConnectionOpacity(clamped);
    };

    return (
        <div className="panel-controls rect-node-connections-controls">
            <div className="tool-header">
                <div className="tool-header-title-row">
                    <h2>Rect Node Connections</h2>
                    <ToolDescriptionPopover dialogLabel="Rect Node Connections tool description">
                        <p>
                            Click on the canvas to add nodes. Each node is a
                            rectangle with both diagonals drawn (width/height
                            aspect ratio can vary per node). By default, pairs
                            within the connection distance are linked; switch to
                            insertion order to chain consecutive nodes in the
                            order they were added.
                        </p>
                    </ToolDescriptionPopover>
                </div>
                <div className="tool-header-actions">
                    <UndoRedoControls
                        canUndo={canUndo}
                        canRedo={canRedo}
                        onUndo={undo}
                        onRedo={redo}
                    />
                    <button
                        type="button"
                        className="generic-button"
                        onClick={resetParameters}
                    >
                        Reset parameters
                    </button>
                    <button
                        type="button"
                        className="generic-button"
                        onClick={handleDownload}
                        disabled={nodes.length === 0}
                    >
                        Download SVG
                    </button>
                    <button
                        type="button"
                        className="generic-button"
                        onClick={clearNodes}
                        disabled={nodes.length === 0}
                    >
                        Clear all nodes
                    </button>
                </div>
            </div>
            <div className="path-tool-inputs">
                <PresetSelector
                    toolId="rect-node-connections"
                    onApply={applyPreset}
                    onExport={getPresetValues}
                />
                <p className="control-panel-hint">
                    {nodes.length === 0
                        ? "Click the canvas to place nodes."
                        : `${nodes.length} node${nodes.length === 1 ? "" : "s"} placed.`}
                </p>
                <CollapsibleToolGroup title="Connections" icon={Link2}>
                    <div className="field-row field-row--switch">
                        <Switch
                            checked={connectionLinkMode === "insertion_order"}
                            onChange={(on) =>
                                setConnectionLinkMode(
                                    on ? "insertion_order" : "proximity",
                                )
                            }
                            label="Insertion order (chain consecutive nodes)"
                            title="On: link each node to the next in add order. Off: link all pairs closer than the distance threshold."
                        />
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Connection distance threshold (px)
                            <DebouncedInput
                                type="number"
                                min={1}
                                step={10}
                                value={connectionDistanceThreshold}
                                onChange={handleConnectionDistanceChange}
                                disabled={connectionLinkMode === "insertion_order"}
                            />
                        </label>
                    </div>
                    {connectionLinkMode === "insertion_order" && (
                        <p className="control-panel-hint">
                            Distance is not used; edges follow add order only.
                        </p>
                    )}
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Brush & nodes" icon={Paintbrush}>
                    <div className="field-row">
                        <label className="field-label">
                            Seed
                            <DebouncedInput
                                type="text"
                                value={seed}
                                onChange={(v) => setSeed(String(v))}
                                spellCheck={false}
                                autoComplete="off"
                                title="Same seed + same click sequence reproduces brush scatter and size/aspect variation."
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Brush size (px, 0 = single node per click)
                            <DebouncedInput
                                type="number"
                                min={0}
                                step={5}
                                value={brushSize}
                                onChange={handleBrushSizeChange}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Brush density (nodes per click)
                            <DebouncedInput
                                type="number"
                                min={0}
                                step={0.1}
                                value={brushDensity}
                                onChange={handleBrushDensityChange}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Node half-size (px, center to edge)
                            <DebouncedInput
                                type="number"
                                min={1}
                                step={1}
                                value={nodeBaseSize}
                                onChange={handleNodeBaseSizeChange}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Node size randomness (0-1)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={1}
                                step={0.1}
                                value={nodeSizeRandomness}
                                onChange={handleNodeSizeRandomnessChange}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Width/height aspect ratio (0 = square, 1 = varied)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={1}
                                step={0.1}
                                value={nodeWidthHeightRandomness}
                                onChange={handleNodeWidthHeightRandomnessChange}
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Appearance" icon={Palette}>
                    <div className="field-row">
                        <CheckboxField
                            checked={nodeFillEnabled}
                            onChange={setNodeFillEnabled}
                            label="Fill node squares"
                            title="When off, only strokes and diagonals are drawn."
                        />
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Node fill color
                            <input
                                type="color"
                                value={nodeColor}
                                onChange={(e) => setNodeColor(e.target.value)}
                                className="control-color-block"
                                disabled={!nodeFillEnabled}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Stroke color
                            <input
                                type="color"
                                value={strokeColor}
                                onChange={(e) =>
                                    setStrokeColor(e.target.value)
                                }
                                className="control-color-block"
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Connection opacity
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={1}
                                step={0.1}
                                value={connectionOpacity}
                                onChange={handleConnectionOpacityChange}
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>
                <CollapsibleToolGroup title="Send to tool" icon={Share2}>
                    <SendSvgToToolMenu
                        disabled={nodes.length === 0}
                        getSvg={() => ({
                            name: "rect-node-connections-export.svg",
                            content: buildExportSvgString(),
                        })}
                        allowedSlots={["path", "object", "primary"]}
                        excludeTargetPanels={["rect-node-connections"]}
                    />
                </CollapsibleToolGroup>
            </div>
        </div>
    );
}
